import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { auth, db } from '../firebase/firebase';
import { collection, getDocs, doc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import WIcon from '../components/shared/WIcon';
import Modal from '../components/shared/Modal';
import '../styles/VendorAdmin.css';

const initialsFor = (name) => (name || '—').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const AddVendorModal = ({ onClose, onSaved }) => {
  const [formData, setFormData] = useState({ email: '', businessName: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => {
    setFormData((f) => ({ ...f, [k]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = formData.email.toLowerCase().trim();
    const businessName = formData.businessName.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!businessName) {
      setError('Business name is required.');
      return;
    }

    setSubmitting(true);
    try {
      const existing = await getDoc(doc(db, 'vendorAdmins', email));
      if (existing.exists()) {
        throw new Error('This email is already a whitelisted vendor.');
      }

      await setDoc(doc(db, 'vendorAdmins', email), {
        email,
        businessName,
        vendorId: null,
        status: 'pending',
        addedAt: new Date(),
        addedBy: auth.currentUser?.email || null,
      });

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Error whitelisting vendor:', err);
      setError(err.message || 'Failed to whitelist vendor.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Add Vendor" onClose={onClose} width={460}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="loysta-alert error">{error}</div>}

        <div className="form-group">
          <label>Business Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.businessName}
            onChange={setField('businessName')}
            placeholder="e.g. Coffee House Georgia"
            required
          />
        </div>
        <div className="form-group">
          <label>Vendor Google email *</label>
          <input
            type="email"
            className="form-control"
            value={formData.email}
            onChange={setField('email')}
            placeholder="vendor@business.com"
            required
          />
          <span className="form-hint">
            Whitelisted for Google sign-in. The vendor signs in with this Google account to access the vendor portal.
          </span>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" className="cancel-btn" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
            {submitting ? 'Saving…' : 'Add Vendor'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Vendors = () => {
  const { registerPageContext } = useOutletContext();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      const [whitelistSnap, vendorsSnap] = await Promise.all([
        getDocs(collection(db, 'vendorAdmins')),
        getDocs(collection(db, 'vendors')),
      ]);

      const vendorsByUid = Object.fromEntries(
        vendorsSnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]),
      );

      const list = whitelistSnap.docs.map((d) => {
        const wl = { id: d.id, ...d.data() };
        const linkedVendor = wl.vendorId ? vendorsByUid[wl.vendorId] : null;
        return {
          email: wl.email || d.id,
          businessName: linkedVendor?.businessName || wl.businessName || '—',
          vendorId: wl.vendorId || null,
          status: wl.status || (wl.vendorId ? 'active' : 'pending'),
          isActive: linkedVendor ? linkedVendor.isActive !== false : false,
          logoUrl: linkedVendor?.logoUrl || null,
          addedAt: wl.addedAt,
        };
      });

      setVendors(list);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  useEffect(() => {
    registerPageContext({
      addLabel: 'Add Vendor',
      onAdd: () => setShowAdd(true),
    });
  }, [registerPageContext]);

  const handleDelete = async (email) => {
    const v = vendors.find((x) => x.email === email);
    if (!window.confirm(`Remove vendor "${v?.businessName || email}"? They will lose access to the vendor portal.`)) return;
    try {
      // Always remove the whitelist entry. If the vendor has signed in and a
      // canonical vendor doc exists, remove that too. (Offers and other
      // downstream artefacts are kept; the super admin can clean those up
      // separately.)
      await deleteDoc(doc(db, 'vendorAdmins', email));
      if (v?.vendorId) {
        try { await deleteDoc(doc(db, 'vendors', v.vendorId)); } catch (_) { /* ignore */ }
      }
      fetchVendors();
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = vendors.filter((v) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (v.businessName || '').toLowerCase().includes(s) || (v.email || '').toLowerCase().includes(s);
  });

  return (
    <>
      {showAdd && <AddVendorModal onClose={() => setShowAdd(false)} onSaved={fetchVendors} />}

      <div className="loysta-card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>{filtered.length} {filtered.length === 1 ? 'vendor' : 'vendors'}</span>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#00bf63', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer', transition: 'background 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
          >
            <WIcon name="plus" size={14} color="white" />
            Add Vendor
          </button>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading vendors…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {vendors.length === 0 ? 'No vendors yet. Click "Add Vendor" to whitelist one.' : 'No vendors match your search.'}
              </td></tr>
            ) : filtered.map((v) => {
              const pending = v.status === 'pending';
              const statusClass = pending ? 'paused' : (v.isActive ? 'active' : 'inactive');
              const statusLabel = pending ? 'Pending first sign-in' : (v.isActive ? 'Active' : 'Inactive');
              return (
                <tr key={v.email}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {v.logoUrl ? (
                        <img src={v.logoUrl} alt={v.businessName} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(0,191,99,0.10)', color: '#00bf63', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{initialsFor(v.businessName)}</div>
                      )}
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{v.businessName || 'Unnamed'}</span>
                    </div>
                  </td>
                  <td style={{ color: '#334155' }}>{v.email}</td>
                  <td><span className={`loysta-status ${statusClass}`}>{statusLabel}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="loysta-icon-btn danger" onClick={() => handleDelete(v.email)} title="Remove">
                        <WIcon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Vendors;
